import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Lock, Star, Clock } from 'lucide-react';
import FavoriteButton from './FavoriteButton';

const getCountdown = (releaseDateStr) => {
  if (!releaseDateStr) return null;
  const now = new Date();
  const release = new Date(releaseDateStr);
  const diff = release - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${mins}m`;
};

const ContentCard = ({ content }) => {
  const navigate = useNavigate();
  const isLocked = content.is_locked;
  const [countdown, setCountdown] = useState(() => getCountdown(content.release_date));

  useEffect(() => {
    if (!isLocked || !content.release_date) return;
    const interval = setInterval(() => {
      setCountdown(getCountdown(content.release_date));
    }, 60000);
    return () => clearInterval(interval);
  }, [isLocked, content.release_date]);

  return (
    <div
      className="group relative cursor-pointer rounded-2xl overflow-hidden bg-white/5 backdrop-blur-2xl border border-white/20 transition-all duration-300 hover:scale-105 hover:border-electric/60 hover:shadow-2xl hover:shadow-electric/40 hover:bg-white/10"
      style={{ backdropFilter: 'blur(30px) saturate(180%)' }}
      onClick={() => navigate(`/content/${content.id}`)}
      data-testid={`content-card-${content.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={content.thumbnail}
          alt={content.title}
          className={`w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-110 ${isLocked && content.content_type === 'tv_show' ? 'blur-sm brightness-50' : ''}`}
        />

        {/* Lock overlay only for TV show episodes */}
        {isLocked && content.content_type === 'tv_show' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30">
            <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 mb-2">
              <Lock className="h-6 w-6 text-white" />
            </div>
            {countdown && (
              <span className="text-xs font-medium text-white/90 bg-black/50 px-2 py-1 rounded-full">{countdown}</span>
            )}
          </div>
        )}
        
        {/* Hover overlay - show for unlocked OR locked non-TV content */}
        {(!isLocked || content.content_type !== 'tv_show') && (
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-electric/90 flex items-center justify-center backdrop-blur-sm">
                {content.coming_soon ? (
                  <Clock className="h-6 w-6 text-background" />
                ) : (
                  <Play className="h-6 w-6 text-background fill-background" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Release Date Badge */}
        {isLocked && content.release_date && (
          <div className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-500 backdrop-blur-xl px-3 py-1.5 rounded-lg flex items-center space-x-1 border border-white/30 shadow-lg" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
            <Clock className="h-3 w-3 text-white" />
            <span className="text-xs font-semibold text-white">{countdown || 'Coming Soon'}</span>
          </div>
        )}

        {/* Coming Soon Badge (no release date) */}
        {content.coming_soon && !isLocked && !content.release_date && (
          <div className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-500 backdrop-blur-xl px-3 py-1.5 rounded-lg flex items-center space-x-1 border border-white/30 shadow-lg" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
            <Clock className="h-3 w-3 text-white" />
            <span className="text-xs font-semibold text-white">Coming Soon</span>
          </div>
        )}

        {/* Premium Badge - only show if not locked/coming soon */}
        {content.is_premium && !isLocked && !content.coming_soon && (
          <div className="absolute top-2 right-2 bg-electric/95 backdrop-blur-xl px-3 py-1.5 rounded-lg flex items-center space-x-1 border border-white/30 shadow-lg" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
            <Lock className="h-3 w-3 text-background" />
            <span className="text-xs font-semibold text-background">Premium</span>
          </div>
        )}

        {/* Featured Badge */}
        {content.featured && !isLocked && !content.coming_soon && (
          <div className="absolute top-2 left-2 bg-pulse/95 backdrop-blur-xl px-3 py-1.5 rounded-lg border border-white/30 shadow-lg" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
            <span className="text-xs font-semibold text-background">Featured</span>
          </div>
        )}

        {/* Favorite Button - Top Right */}
        {!isLocked && (
          <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <FavoriteButton contentId={content.id} size="sm" />
          </div>
        )}

        {/* Watch Progress Bar */}
        {content.watch_progress && !isLocked && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
            <div
              className="h-full bg-electric"
              style={{ width: `${content.watch_progress.percentage}%` }}
            />
          </div>
        )}
      </div>

      {/* Content Info */}
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground line-clamp-1 mb-1">
          {content.title}
        </h3>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{content.year}</span>
          <div className="flex items-center space-x-1">
            <Star className="h-3 w-3 text-electric fill-electric" />
            <span>{content.rating?.toFixed(1) || '0.0'}</span>
          </div>
        </div>
        
        <div className="mt-2 flex flex-wrap gap-1">
          {content.genre?.slice(0, 2).map((genre, index) => (
            <span
              key={index}
              className="text-xs px-2 py-0.5 rounded-full bg-secondary text-foreground/70"
            >
              {genre}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContentCard;
